import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Star, Images } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-4 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = "w-full bg-white/5 border border-white/15 px-4 py-4 text-base text-white focus:outline-none focus:border-primary appearance-none";

/** Website photo/video gallery (shown on the public Projects pages). */
export const ProjectPicturesPanel = () => {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newCategory, setNewCategory] = useState("residential");
  const [newFiles, setNewFiles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("residential");
  const newFileInput = useRef(null);
  const addFileInput = useRef(null);

  const loadProjects = useCallback(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const openProject = async (id) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setSelected(data);
      setEditTitle(data.title);
      setEditCategory(data.category);
    } catch {
      toast.error("Could not open project");
    }
  };

  const uploadFiles = async (projectId, files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    await api.post(`/admin/projects/${projectId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };

  const createProject = async () => {
    if (!newTitle.trim()) return toast.error("Give the project a title first");
    setCreating(true);
    try {
      const { data: project } = await api.post("/admin/projects", { title: newTitle, category: newCategory });
      if (newFiles.length) await uploadFiles(project.id, newFiles);
      toast.success("Project is live on the website!");
      setNewTitle("");
      setNewFiles([]);
      setShowNew(false);
      loadProjects();
      openProject(project.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const addPhotos = async (files) => {
    if (!files.length) return;
    setAdding(true);
    try {
      await uploadFiles(selected.id, files);
      toast.success("Photos added to the project");
      openProject(selected.id);
      loadProjects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
      if (addFileInput.current) addFileInput.current.value = "";
    }
  };

  const saveProject = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/projects/${selected.id}`, { title: editTitle, category: editCategory });
      toast.success("Project updated");
      openProject(selected.id);
      loadProjects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setCover = async (mediaId) => {
    try {
      await api.patch(`/admin/projects/${selected.id}`, { cover_media_id: mediaId });
      toast.success("Cover photo updated");
      openProject(selected.id);
      loadProjects();
    } catch {
      toast.error("Could not set cover");
    }
  };

  const removeMedia = async (mediaId) => {
    try {
      await api.delete(`/admin/media/${mediaId}`);
      toast.success("Photo removed");
      openProject(selected.id);
      loadProjects();
    } catch {
      toast.error("Could not remove");
    }
  };

  const removeProject = async () => {
    if (!window.confirm(`Delete "${selected.title}" and all its photos?`)) return;
    try {
      await api.delete(`/admin/projects/${selected.id}`);
      toast.success("Project deleted");
      setSelected(null);
      loadProjects();
    } catch {
      toast.error("Could not delete project");
    }
  };

  if (selected) {
    return (
      <div data-testid="admin-project-edit" className="space-y-5">
        <button onClick={() => setSelected(null)} data-testid="project-back" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary transition-colors duration-300">
          <ArrowLeft size={15} aria-hidden="true" /> All Projects
        </button>
        <div className="border border-white/10 p-4 space-y-3">
          <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="edit-project-title" className={inputCls} />
          <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} data-testid="edit-project-category" className={selectCls}>
            <option value="residential" className="bg-[#252720]">Residential</option>
            <option value="commercial" className="bg-[#252720]">Commercial</option>
          </select>
          <button onClick={saveProject} disabled={saving} data-testid="edit-project-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 disabled:opacity-50">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
        <input ref={addFileInput} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => addPhotos(Array.from(e.target.files || []))} data-testid="add-photos-input" />
        <button onClick={() => addFileInput.current?.click()} disabled={adding} data-testid="add-photos-button" className="w-full border-2 border-dashed border-white/20 hover:border-primary transition-colors duration-300 py-8 flex flex-col items-center gap-2 text-white/60 disabled:opacity-50">
          <Plus size={22} className="text-primary" aria-hidden="true" />
          <span className="text-sm font-semibold">{adding ? "Uploading..." : "Add more photos to this project"}</span>
        </button>
        {selected.media.length === 0 && <p className="text-white/40 text-sm py-6 text-center">No photos yet — add some above.</p>}
        <div className="grid grid-cols-2 gap-3">
          {selected.media.map((m) => {
            const isCover = m.id === selected.cover_media_id;
            return (
              <div key={m.id} className={`relative border ${isCover ? "border-primary" : "border-white/10"}`} data-testid={`project-photo-${m.id}`}>
                {m.media_type === "video" ? (
                  <video src={mediaUrl(m.storage_path)} muted preload="metadata" className="w-full aspect-square object-cover" />
                ) : (
                  <img src={mediaUrl(m.storage_path)} alt="Project photo" className="w-full aspect-square object-cover" loading="lazy" />
                )}
                {isCover && <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-1">Cover</span>}
                <div className="absolute inset-x-0 bottom-0 bg-[#171914]/75 px-2 py-1.5 flex items-center justify-between">
                  <button onClick={() => setCover(m.id)} data-testid={`set-cover-${m.id}`} aria-label="Set as cover" className={`p-1.5 ${isCover ? "text-primary" : "text-white/60 hover:text-primary"}`}>
                    <Star size={16} fill={isCover ? "currentColor" : "none"} />
                  </button>
                  <button onClick={() => removeMedia(m.id)} data-testid={`delete-photo-${m.id}`} aria-label="Delete photo" className="text-red-400 hover:text-red-300 p-1.5">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={removeProject} data-testid="project-delete" className="w-full border border-red-400/40 text-red-300 font-bold uppercase tracking-widest text-xs py-4 transition-colors duration-300 hover:bg-red-400/10">
          Delete Entire Project
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-projects-tab" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-white/40 text-xs">These photos show on the public website gallery.</p>
        <button onClick={() => setShowNew(!showNew)} data-testid="new-project-toggle" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:-translate-y-0.5 shrink-0">
          <Plus size={15} aria-hidden="true" /> New Project
        </button>
      </div>
      {showNew && (
        <div className="border border-primary/40 bg-primary/5 p-5 space-y-4" data-testid="new-project-form">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">New Project</p>
          <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Project title — e.g. 'Colonial in Stamford, CT'" data-testid="new-project-title" className={inputCls} />
          <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="new-project-category" className={selectCls}>
            <option value="residential" className="bg-[#252720]">Residential</option>
            <option value="commercial" className="bg-[#252720]">Commercial</option>
          </select>
          <input ref={newFileInput} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => setNewFiles(Array.from(e.target.files || []))} data-testid="new-project-files" />
          <button onClick={() => newFileInput.current?.click()} data-testid="new-project-pick" className="w-full border-2 border-dashed border-white/20 hover:border-primary transition-colors duration-300 py-10 flex flex-col items-center gap-3 text-white/60">
            <Images size={26} className="text-primary" aria-hidden="true" />
            <span className="text-sm font-semibold">{newFiles.length ? `${newFiles.length} photo${newFiles.length === 1 ? "" : "s"} selected` : "Tap to select photos — you can pick many at once"}</span>
          </button>
          <p className="text-white/40 text-xs">The first photo becomes the cover — you can change it anytime.</p>
          <button onClick={createProject} disabled={creating} data-testid="new-project-create" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50">
            {creating ? "Creating..." : "Create Project"}
          </button>
        </div>
      )}
      {projects.length === 0 && !showNew && (
        <p className="text-white/40 text-sm py-10 text-center" data-testid="admin-projects-empty">No projects yet. Tap "New Project" to add your first one.</p>
      )}
      <div className="space-y-3">
        {projects.map((p) => (
          <button key={p.id} onClick={() => openProject(p.id)} data-testid={`project-item-${p.id}`} className="w-full border border-white/10 rounded-xl bg-card shadow-[0_4px_20px_rgba(0,0,0,0.3)] p-3 flex items-center gap-4 text-left transition-colors duration-300 hover:border-primary/50">
            {p.cover_path ? (
              <img src={mediaUrl(p.cover_path)} alt={p.title} className="w-16 h-16 object-cover shrink-0" loading="lazy" />
            ) : (
              <div className="w-16 h-16 bg-white/5 flex items-center justify-center shrink-0"><Images size={18} className="text-white/30" aria-hidden="true" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{p.title}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">{p.category} · {p.photo_count} photo{p.photo_count === 1 ? "" : "s"}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
